import React, { useState, useEffect } from 'react';
import { useNotificationSettingsServices } from '../../apis/notificationSettingsService';
import { Save, Bell, FileText, Megaphone, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const NotificationSettingsPage = () => {
  const [settings, setSettings] = useState({
    newsNotifications: true,
    blogNotifications: true,
    advertisementNotifications: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { getNotificationSettings, updateNotificationSettings, loading, error } = useNotificationSettingsServices();

  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  // Show error if API call fails
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const response = await getNotificationSettings();
      if (response.success && response.data) {
        setSettings({
          newsNotifications: response.data.newsNotifications ?? true,
          blogNotifications: response.data.blogNotifications ?? true,
          advertisementNotifications: response.data.advertisementNotifications ?? true
        });
      } else {
        toast.error(response.message || 'Failed to fetch notification settings');
      }
    } catch (err) {
      console.error('Error fetching notification settings:', err);
      toast.error('Failed to load notification settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings({
      ...settings,
      [key]: !settings[key]
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await updateNotificationSettings(settings);
      if (response.success) {
        toast.success('Notification settings updated successfully');
        setSettings({
          newsNotifications: response.data.newsNotifications,
          blogNotifications: response.data.blogNotifications,
          advertisementNotifications: response.data.advertisementNotifications
        });
      } else {
        toast.error(response.message || 'Failed to update notification settings');
      }
    } catch (err) {
      console.error('Error updating notification settings:', err);
      toast.error('Failed to update notification settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading notification settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Notification Settings</h1>
          <p className="text-gray-500 mt-1">Control which notifications are sent to subscribers</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <Save size={18} className="mr-2" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
        <AlertCircle className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">How it works:</p>
          <p>When enabled, subscribers will receive email notifications when new content is published. When disabled, no notifications will be sent for that content type.</p>
        </div>
      </div>

      {/* Settings Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* News Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg mr-3">
                <FileText className="text-blue-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">News Notifications</h3>
                <p className="text-sm text-gray-500">New news articles</p>
              </div>
            </div>
            <div className={`flex items-center justify-center w-14 h-7 rounded-full transition-colors ${
              settings.newsNotifications ? 'bg-green-500' : 'bg-gray-300'
            }`}>
              <button
                onClick={() => handleToggle('newsNotifications')}
                className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                  settings.newsNotifications ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </div>
          </div>
          <div className="flex items-center text-sm">
            {settings.newsNotifications ? (
              <>
                <CheckCircle className="text-green-500 mr-2" size={16} />
                <span className="text-green-700">Enabled</span>
              </>
            ) : (
              <>
                <AlertCircle className="text-gray-400 mr-2" size={16} />
                <span className="text-gray-500">Disabled</span>
              </>
            )}
          </div>
        </div>

        {/* Blog Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg mr-3">
                <FileText className="text-purple-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Blog Notifications</h3>
                <p className="text-sm text-gray-500">New blog posts</p>
              </div>
            </div>
            <div className={`flex items-center justify-center w-14 h-7 rounded-full transition-colors ${
              settings.blogNotifications ? 'bg-green-500' : 'bg-gray-300'
            }`}>
              <button
                onClick={() => handleToggle('blogNotifications')}
                className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                  settings.blogNotifications ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </div>
          </div>
          <div className="flex items-center text-sm">
            {settings.blogNotifications ? (
              <>
                <CheckCircle className="text-green-500 mr-2" size={16} />
                <span className="text-green-700">Enabled</span>
              </>
            ) : (
              <>
                <AlertCircle className="text-gray-400 mr-2" size={16} />
                <span className="text-gray-500">Disabled</span>
              </>
            )}
          </div>
        </div>

        {/* Advertisement Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg mr-3">
                <Megaphone className="text-orange-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Advertisement Notifications</h3>
                <p className="text-sm text-gray-500">New advertisements</p>
              </div>
            </div>
            <div className={`flex items-center justify-center w-14 h-7 rounded-full transition-colors ${
              settings.advertisementNotifications ? 'bg-green-500' : 'bg-gray-300'
            }`}>
              <button
                onClick={() => handleToggle('advertisementNotifications')}
                className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                  settings.advertisementNotifications ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </div>
          </div>
          <div className="flex items-center text-sm">
            {settings.advertisementNotifications ? (
              <>
                <CheckCircle className="text-green-500 mr-2" size={16} />
                <span className="text-green-700">Enabled</span>
              </>
            ) : (
              <>
                <AlertCircle className="text-gray-400 mr-2" size={16} />
                <span className="text-gray-500">Disabled</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Current Settings Summary</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">News Notifications:</span>
            <span className={`font-medium ${settings.newsNotifications ? 'text-green-600' : 'text-gray-400'}`}>
              {settings.newsNotifications ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Blog Notifications:</span>
            <span className={`font-medium ${settings.blogNotifications ? 'text-green-600' : 'text-gray-400'}`}>
              {settings.blogNotifications ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Advertisement Notifications:</span>
            <span className={`font-medium ${settings.advertisementNotifications ? 'text-green-600' : 'text-gray-400'}`}>
              {settings.advertisementNotifications ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;

